// @ts-strict
import {useEffect, useRef, useState} from 'react';
import {Typography} from '@ezcater/tapas';
import {ErrorBoundary} from '@sentry/nextjs';
import {useRouter} from 'next/router';
import useTranslation from 'next-translate/useTranslation';

import useIdentity from '@/hooks/useIdentity';
import useTracking from '@/hooks/useTracking';
import {SUPPORT_MESSAGE_SENT_PATH} from '@/paths';
import {compilePath} from '@/utils';
import {
  parseSupportMessageSource,
  SupportMessageForm,
  useSubmitSupportCase,
} from './SupportMessage';

const SupportMessageErrorFallback = (): React.ReactElement => {
  const {t} = useTranslation('common');

  return (
    <div className="flex flex-col gap-2 text-center" data-testid="support-message-error">
      <p className="font-bold">{t('components.SupportMessage.error.title')}</p>
      <p className="text-sm text-peppercorn-600">{t('components.SupportMessage.error.body')}</p>
    </div>
  );
};

const readPrefillParam = (value: string | string[] | undefined): string =>
  typeof value === 'string' ? value : '';

type SupportMessagePageRootProps = {
  navigate?: (url: string) => void;
};

const SupportMessagePageRoot = ({navigate}: SupportMessagePageRootProps): React.ReactElement => {
  const {t} = useTranslation('common');
  const router = useRouter();
  // Replaces rather than pushes: back from the confirmation should leave the webview, not return
  // to a filled form that could be submitted again.
  const goToConfirmation = navigate ?? ((url: string) => void router.replace(url));
  const {data, isLoggedIn} = useIdentity();
  const {track} = useTracking();
  const source = parseSupportMessageSource(router.query.source);
  const submitSupportCase = useSubmitSupportCase();
  const trackedOpen = useRef(false);

  const [prefill] = useState(() => ({
    fullName: readPrefillParam(router.query.name),
    email: readPrefillParam(router.query.email),
  }));

  useEffect(() => {
    const {name: _name, email: _email, ...rest} = router.query;
    if (_name === undefined && _email === undefined) return;

    router.replace({pathname: router.pathname, query: rest}, undefined, {shallow: true});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query.name, router.query.email]);

  useEffect(() => {
    if (trackedOpen.current) return;
    trackedOpen.current = true;

    track('support-message-modal-opened', {
      page: window.location.pathname,
      misc_json: JSON.stringify({
        is_logged_in: isLoggedIn,
        surface: 'standalone_route',
        ...(source ? {source} : {}),
      }),
    });
  }, [track, isLoggedIn, source]);

  return (
    <main
      className="mx-auto flex min-h-dvh w-full max-w-[640px] flex-col gap-6 bg-white px-4 py-6 text-peppercorn-800"
      data-testid="support-message-page"
    >
      <Typography render={<h1>{t('components.SupportMessage.title')}</h1>} variant="heading-lg" />

      <ErrorBoundary fallback={<SupportMessageErrorFallback />} showDialog={false}>
        <SupportMessageForm
          fieldContext={{
            fullName: data?.me?.consumerAccount?.fullName || prefill.fullName,
            email: data?.me?.consumerAccount?.email || prefill.email,
          }}
          onSubmit={submitSupportCase}
          onSuccess={() => {
            goToConfirmation(compilePath(SUPPORT_MESSAGE_SENT_PATH, {}));
          }}
          source={source}
          surface="standalone_route"
        />
      </ErrorBoundary>
    </main>
  );
};

export default SupportMessagePageRoot;
